import {
  Box,
  Button,
  HeadingText,
  ImageMessage,
  ParagraphText,
  StackLayout,
  UtilityText,
  useMediaQuery,
  queryHelpers,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useForm, FormProvider } from "@leagueplatform/web-common";
import allowTextNotificationsIcon from "assets/allow-text-notifications.svg";
import { phoneNumberValidation } from "common/utils/rhf-form-validation.util";
import { RHFTextInput } from "components/form-elements/rhf-text-input.component";
import { useUpdateCommunicationPreferences } from "hooks/mutations/use-update-communication-preferences.hook";
import { useProfileData } from "hooks/use-profile-data.hook";

type NotificationsFormValues = { mobilePhoneNumber: string };

type NotificationsStepProps = {
  onContinue: () => void;
  onSkip: () => void;
  onError?: (error: Error) => void;
};

export const NotificationsStep = ({
  onContinue,
  onSkip,
  onError,
}: NotificationsStepProps) => {
  const { formatMessage } = useIntl();

  const { data: profileData } = useProfileData();
  const formMethods = useForm<NotificationsFormValues>({
    defaultValues: { mobilePhoneNumber: profileData?.mobilePhoneNumber || "" },
  });

  const isMobile = useMediaQuery(queryHelpers.down("tablet"));

  const { updateCommunicationPreferences, isLoading } =
    useUpdateCommunicationPreferences({
      onError,
    });

  const handleSubmit = async (data: NotificationsFormValues) => {
    await updateCommunicationPreferences({
      textNotification: true,
      mobilePhoneNumber: data.mobilePhoneNumber,
    });
    onContinue();
  };

  return (
    <FormProvider formMethods={formMethods} onSubmit={handleSubmit}>
      <StackLayout horizontalAlignment="center">
        <ImageMessage
          image={allowTextNotificationsIcon}
          css={{
            padding: isMobile ? "$oneAndHalf" : "$two",
            maxWidth: "380px",
          }}
        >
          <HeadingText
            level="2"
            size="xl"
            css={{ textAlign: "center", marginBlockEnd: "$oneAndHalf" }}
          >
            {formatMessage({ id: "STR_ENABLE_TEXT_NOTIFICATIONS" })}
          </HeadingText>
          <StackLayout spacing="$none" horizontalAlignment="center">
            <ParagraphText
              emphasis="subtle"
              css={{ "&&": { marginBlockEnd: "$half", textAlign: "center" } }}
            >
              {formatMessage({ id: "STR_ACCOUNT_SETUP_NOTIFICATIONS_BODY" })}
            </ParagraphText>
            <Box
              as="ul"
              css={{
                marginBlockEnd: "$oneAndHalf",
                listStyle: "disc",
                listStylePosition: "inside",
                textAlign: "center",
              }}
            >
              <UtilityText as="li" emphasis="subtle">
                {formatMessage({
                  id: "STR_ACCOUNT_SETUP_NOTIFICATIONS_BULLET_EVENTS",
                })}
              </UtilityText>
              <UtilityText as="li" emphasis="subtle">
                {formatMessage({
                  id: "STR_ACCOUNT_SETUP_NOTIFICATIONS_BULLET_REMINDERS",
                })}
              </UtilityText>
              <UtilityText as="li" emphasis="subtle">
                {formatMessage({
                  id: "STR_ACCOUNT_SETUP_NOTIFICATIONS_BULLET_RESULTS",
                })}
              </UtilityText>
            </Box>
            {/* TODO QUEST-1160 Prefill mobile phone number from user profile */}
            <RHFTextInput
              id="mobilePhoneNumber"
              type="tel"
              required
              label={formatMessage({ id: "STR_MOBILE_PHONE_NUMBER" })}
              registerOptions={phoneNumberValidation(
                formatMessage({ id: "STR_PHONE_VALIDATION_ERROR" })
              )}
            />
          </StackLayout>
        </ImageMessage>
        <StackLayout
          horizontalAlignment="spaceBetween"
          orientation={isMobile ? "vertical" : "horizontal"}
          css={{
            borderTop: "1px solid $onSurfaceBorderSubdued",
            paddingBlock: "$one",
            paddingInline: isMobile ? "$oneAndHalf" : "$two",
            width: "100%",
          }}
        >
          {/* Swap button order in mobile */}
          {isMobile ? (
            <StackLayout spacing="$one">
              <Button type="submit" width="fillContainer" loading={isLoading}>
                {formatMessage({ id: "STR_CONTINUE" })}
              </Button>
              <Button
                priority="tertiary"
                onClick={onSkip}
                width="fillContainer"
              >
                {formatMessage({ id: "STR_NOT_NOW" })}
              </Button>
            </StackLayout>
          ) : (
            <>
              <Button priority="tertiary" onClick={onSkip}>
                {formatMessage({ id: "STR_NOT_NOW" })}
              </Button>
              {/* Button content loses alignment if a fixed width is set directly on Button. */}
              <Box css={{ minWidth: "220px" }}>
                <Button type="submit" width="fillContainer" loading={isLoading}>
                  {formatMessage({ id: "STR_CONTINUE" })}
                </Button>
              </Box>
            </>
          )}
        </StackLayout>
      </StackLayout>
    </FormProvider>
  );
};
